import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Portal from './Portal';
import classNames from 'classnames';
import _ from 'lodash';
import SearchUtil from '../util/SearchUtil';
import ViewUtil from '../ViewUtil';
import { TextWithEllipsisFileExtension } from './TextWithEllipsis';
import { Dd, Div } from './elements';
import { getInnerWidth, isMobile } from '../util/ScreenUtil';
import Button from './Button';
import Separator from './form/Separator';
import Store from '../store/Store';
import {
  ASSET_FILTERS,
  GLOBAL_SEARCH_INFO,
  SELECTED_COLLECTION,
  SELECTED_COLLECTION_ID,
  SHOW_FOLDERNAV_VIEW
} from '../store/Constants';
import { ThemeSetup } from '../themes/ThemeSetup';
import { clearAssetFilters } from '../store/StoreUtil';

import './AutoCompleteGlobalSearch.css';
import StringUtil from '../util/StringUtil';
import StoreListener from '../store/StoreListener';
import t from '../i18n/I18n';
import SaveSettings from '../queries/settings/SaveSettings';

const debug = require('../debug/debug')('wc:common:AutoCompleteGlobalSearch');

let _store = {
  query: ''
};

const storeItems = [GLOBAL_SEARCH_INFO];
const SEARCH_SOURCE = {
  SEARCH: 'search',
  ASSET: 'asset',
  COLLECTION: 'collection'
};


@StoreListener(storeItems)
export default class AutoCompleteGlobalSearch extends Component {
  autoCompleteQueryController = null;
  static propTypes = {
    searchedAssets: PropTypes.array,
    searchedCollections: PropTypes.array,
    maxSearchResult: PropTypes.number,
    onClearSearch: PropTypes.func,
    onAutoSearchSuccess: PropTypes.func,
    onAutoSearchError: PropTypes.func,
    onSearchSuccess: PropTypes.func,
    onSearchError: PropTypes.func,
    onCloseMobileNav: PropTypes.func,
    onClearSelectedItems: PropTypes.func
  };

  static defaultProps = {
    maxSearchResult: 7,
    onCloseMobileNav: _.noop
  };

  constructor(props) {
    super(props);
    this.state = {
      searchFoucs: false,
      searchNavigation: false,
      query: _store.query,
      recentSearches: [],
      loading: false
    };
    this.onAutoSearch = _.debounce(this.onAutoSearch.bind(this), 500);
    this.searchSource = SEARCH_SOURCE.SEARCH;
  }

  componentWillMount() {
    window.gs.AutoCompleteGlobalSearchRef = this;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps[GLOBAL_SEARCH_INFO].searchString !== this.props[GLOBAL_SEARCH_INFO].searchString) {
      const searchString = nextProps[GLOBAL_SEARCH_INFO].searchString;
      this.setState({
        query: searchString
      });
      this.refs.search.value = searchString ? searchString : '';
    }
  }

  componentWillUnmount() {
    delete window.gs.AutoCompleteGlobalSearchRef;
    _store.query = this.state.query;
    debug('[componentWillUnmount()] _store.query: ', _store.query);
  }

  getSearchQuery = async (searchString: string) => {
    const { onSearchSuccess } = this.props;
    try {
      // This query is executed when you submit search form, its a general uber search
      debug('[getSearchQuery()] running searchQuery for string: ', searchString);

      // Check if the recent search is empty or already present recentSearches array; if found then neglect.
      const recentSearches = this.state.recentSearches.slice();
      if (
        searchString.trim() !== '' &&
        this.state.recentSearches.findIndex((search) => search === searchString) === -1
      ) {
        //Only update recent string array when different search strings
        recentSearches.unshift(searchString);
      }

      this.setState({ searchNavigation: false, query: searchString, recentSearches });
      if (StringUtil.isNotEmpty(searchString)) {
        const showFolderNavView = Store.get(SHOW_FOLDERNAV_VIEW);
        const globalSearchInfo = Store.get(GLOBAL_SEARCH_INFO);
        if (!showFolderNavView) {
          Store.put(GLOBAL_SEARCH_INFO, { ...globalSearchInfo, searchString });
        }
        let res;
        //if (this.searchSource == SEARCH_SOURCE.ASSET) {
        // res = await SearchUtil.searchQuery(`"${searchString}"`);
        //} else {
        let containerRef = null;
        if(SaveSettings.get(SaveSettings.DEFAULT_VIEW) === 'LIST') {
          containerRef = document.querySelector('[class="list-container"]');
        } else {
          containerRef = document.querySelector('[name="GridView"]');
        }
        if(containerRef) {
          containerRef.scrollTop = 0;
        }
        res = await SearchUtil.searchQuery(`${searchString}`);
        //}
        debug('[getSearchQuery()] searchQuery response: ', res);
        onSearchSuccess(res, searchString);
        // Store.put(GLOBAL_SEARCH_INFO, {
        //   active: true,
        //   assets: res.getResultFiles(),
        //   totalCount: res.getTotalFiles(),
        //   hasNextPage: true,
        //   searchString,
        //   timestamp: Date.now()
        // });
        this.switchToAssetsTab();
      }
    } catch (err) {
      debug('[getSearchQuery()] error: ', err);
      Store.put(GLOBAL_SEARCH_INFO, {
        active: true,
        assets: [],
        totalCount: 0,
        hasNextPage: false,
        searchString
      });
      this.switchToAssetsTab();
    }
  };

  abortRequest = () => {
    if(this.autoCompleteQueryController) {
      this.autoCompleteQueryController.abort();
    }
  }

  getController = (controller) => {
    this.abortRequest();
    this.autoCompleteQueryController = controller;
  }

  async getAutoSearchQuery(searchString) {
    // Its an autosearch query executed on each key press
    debug('[getSearchQuery()] running searchAutoCompleteQuery for string: ', searchString);
    const { onAutoSearchSuccess, onAutoSearchError } = this.props;
    const autoSearch = SearchUtil.searchAutoCompleteQuery(searchString,undefined, null, this.getController);
    this.setState({ searchNavigation: false });
    await autoSearch.then(
      (resp) => {
        onAutoSearchSuccess(resp);
        this.setState({ loading: false });
      },
      (error) => onAutoSearchError(error)
    );
  }

  switchToAssetsTab() {
    this.props.onCloseMobileNav();
    if (!ViewUtil.isAssetDetailViewActive() && ViewUtil.isAssetTabActive()) {
      return;
    }
    debug('[switchToAssetsTab()] calling onCloseMobileNav() and switchToTab("ASSETS")');
    window.gs.blockSearch = true;
    ViewUtil.switchToTab('ASSETS', true);
  }

  onAutoSearch() {
    debug('[onAutoSearch()]');
    // this function is debounced to execute after every 200 ms to limit the backend search call load
    // searchNavigation state is used to check if user is navigating on dropdown using keys up/down, don't issue query in such case
    const { search } = this.refs;
    if (!search) {
      debug('[onAutoSearch()] refs.search is null. return.');
      return;
    }

    const { value } = search;
    const { query, searchNavigation } = this.state;
    if (value !== query && !searchNavigation) {
      this.setState({ loading: true });
      this.getAutoSearchQuery(value, true);
      this.searchSource = SEARCH_SOURCE.SEARCH;
      this.setState({
        query: value
      });
    }
  }

  onSearchSubmit(event) {
    event.preventDefault();
    debug('[onSearchSubmit()] begin');
    // on submit of search form
    const { value } = this.refs.search;
    if (value == '') {
      return;
    }
    const isCollectionSearchedId = this.checkIfSearchedOnCollection(value);
    if (
      isCollectionSearchedId === -1 ||
      (this.searchSource !== SEARCH_SOURCE.COLLECTION && this.props.searchedAssets.length > 0)
    ) {
      debug('[onSearchSubmit()] isCollectionSearchedId === -1. Calling getSearchQuery()');
      this.getSearchQuery(value);
    } else {
      debug('[onSearchSubmit()] calling onCloseMobileNav() and switchToCollectionId()');
      this.props.onCloseMobileNav();
      window.gs.blockSearch = true;
      ViewUtil.switchToCollectionId(this.props.searchedCollections[isCollectionSearchedId].getCollectionId());
    }

    this.refs.search.blur();
    this.onSearchBlur();
  }

  checkIfSearchedOnCollection(searchString) {
    const { searchedCollections } = this.props;
    if (!searchedCollections && searchedCollections.length === 0) {
      return -1;
    }
    return searchedCollections.findIndex(
      (collection) => collection.getName().toUpperCase() === searchString.toUpperCase()
    );
  }

  onKeyUp = (event) => {
    const key = event.keyCode || event.which;
    if (key === 27) {
      event.target.blur();
    } else {
      this.onAutoSearch();
    }
  };

  onKeyPress = (event) => {
    const key = event.keyCode || event.which;
    if (key === 40) {
      // navigation state to avoid search query on every navigation toggle
      this.onDownKeyPress();
    } else if (key === 38) {
      this.onUpKeyPress();
    } else {
      this.setState({
        searchNavigation: false
      });
    }
  };

  /*
    Imp: Logic for up and down key
    Find element with class "selected", if done remove its class and add class to its prev/next sibling.
    If no element found with selected class, add class to last/first element in list
    finally update value in text field
  */

  // e.key === 40
  onDownKeyPress() {
    if (!this.refs.searchResults) {
      return;
    }
    // navigation state to avoid search query on every navigation toggle
    this.setState({ searchNavigation: true });
    let selecetdElement = this.refs.searchResults.getElementsByClassName('selected')[0];
    if (!selecetdElement) {
      selecetdElement = this.refs.searchResults.getElementsByTagName('dd')[0];
      selecetdElement.classList.add('selected');
      this.refs.search.value = selecetdElement.firstElementChild.getAttribute('data-title');
    } else {
      selecetdElement.classList.remove('selected');
      selecetdElement = selecetdElement.nextSibling;

      if (!selecetdElement) {
        selecetdElement = this.refs.searchResults.getElementsByTagName('dd')[0];
      }

      if (selecetdElement.tagName === 'DT') {
        selecetdElement = selecetdElement.nextSibling;
      }

      selecetdElement.classList.add('selected');
      this.refs.search.value = selecetdElement.firstElementChild.getAttribute('data-title');
    }
    if (selecetdElement.getAttribute('class').includes('searched-collection')) {
      this.searchSource = SEARCH_SOURCE.COLLECTION;
    } else {
      this.searchSource = SEARCH_SOURCE.ASSET;
    }
  }

  //On up key e.key === 38
  onUpKeyPress() {
    if (!this.refs.searchResults) {
      return;
    }
    this.setState({ searchNavigation: true });
    const el = this.refs.searchResults.getElementsByClassName('selected');
    let selecetdElement = el[0];
    if (!selecetdElement) {
      const elements = this.refs.searchResults.getElementsByTagName('dd');
      selecetdElement = elements[elements.length - 1];
      selecetdElement.classList.add('selected');
      this.refs.search.value = selecetdElement.firstElementChild.getAttribute('data-title');
    } else {
      selecetdElement.classList.remove('selected');
      selecetdElement = selecetdElement.previousSibling;

      if (selecetdElement.tagName === 'DT') {
        selecetdElement = selecetdElement.previousSibling;
      }

      if (!selecetdElement) {
        const elements = this.refs.searchResults.getElementsByTagName('dd');
        selecetdElement = elements[elements.length - 1];
      }

      selecetdElement.classList.add('selected');
      this.refs.search.value = selecetdElement.firstElementChild.getAttribute('data-title');
    }
    if (selecetdElement.getAttribute('class').includes('searched-collection')) {
      this.searchSource = SEARCH_SOURCE.COLLECTION;
    } else {
      this.searchSource = SEARCH_SOURCE.ASSET;
    }
  }

  onSearchFocus = () => {
    this.setState({
      searchFocus: true
    });
  };

  onSearchBlur = () => {
    // slight delay on blur to avoid instant close
    setTimeout(() => {
      debug('[onSearchBlur()] setting searchFocus=false');
      this.setState({
        searchFocus: false
      });

      if (this.refs.search) {
        if (!this.refs.search.value || !this.refs.search.value.trim()) {
          this.resetSearchBox();
        }
      }
    }, 250);
  };

  resetSearchBox = () => {
    debug('[resetSearchBox()]');
    const { issueFacetQuery, onCloseMobileNav, onClearSelectedItems } = this.props;
    this.refs.search.value = '';
    _store.query = '';
    this.setState(
      {
        query: '',
        searchNavigation: false
      },
      () => {
        // this added when there is an error message on auto and you press close icon, it still shows error message, reset everything to initial
        // onClearSearch();
        window.gs.selectedAssets = [];
        window.gs.clearSearch = true; // it is use to maintain filters if reset search box button click on asset details page
        // onCloseMobileNav();

        // Reset filters only for folder mode but maintain on all_assets case
        const fileFilters = Store.get(ASSET_FILTERS);
        if (fileFilters && fileFilters.getFilter2ValueMap().size() > 0) {
          clearAssetFilters();
          issueFacetQuery();
        }
        onClearSelectedItems();
      }
    );
    Store.put(GLOBAL_SEARCH_INFO, { active: false });
  };

  onSearchedAssetSelect(text) {
    debug('[onSearchedAssetSelect()] text: ', text);

    // onMouseDown is used since it triggers before onClick
    if (text && text.indexOf(' ') >= 0) text = `"${text}"`;
    this.refs.search.value = text;
    this.searchSource = SEARCH_SOURCE.ASSET;
    this.setState({
      query: text
    });
    // block search doesn't reset assets to initial
    // Adding quotes for large file name search in ubersearch
    this.getSearchQuery(text);
  }

  onSearchedCollectionSelect(event) {
    const { onClearSelectedItems } = this.props;
    const { textContent } = event.currentTarget;
    debug('[onSearchedCollectionSelect()] textContent: ', textContent);
    onClearSelectedItems();
    const { id } = event.currentTarget.dataset;
    this.refs.search.value = textContent;
    this.setState({
      query: textContent
    });

    this.props.onCloseMobileNav();
    window.gs.blockSearch = true;
    const cid = parseInt(id);
    // Store.put(SELECTED_COLLECTION_ID, cid);
    Store.put(SELECTED_COLLECTION, undefined);
    ViewUtil.switchToCollectionId(cid);
  }

  onAdvancedSearchClick = () => {
    debug('[onAdvancedSearchClick()] calling ViewUtil.switchToAdvancedSearch()');
    let searchString;
    if (this.refs.search) {
      searchString = this.refs.search.value.trim();
      this.refs.search.value = '';
    }
    this.props.onCloseMobileNav();
    ViewUtil.switchToAdvancedSearch(searchString);
  };

  renderAutoCompleteData() {
    const { searchedAssets, searchedCollections } = this.props;
    const { recentSearches } = this.state;
    const searchString = this.refs.search ? this.refs.search.value.trim() : '';

    const rect = window.searchBlock ? window.searchBlock.getBoundingClientRect() : {};
    const styles = getStyle();
    const style = isMobile()
      ? {
          ...styles.searchContainer.searchDropDown,
          top: 0,
          left: 82,
          width: getInnerWidth() - 107 > 400 ? 400 : getInnerWidth() - 107,
          height: 'auto',
          background: 'var(--primary-header-bg)'
        }
      : {
          ...styles.searchContainer.searchDropDown,
          top: 0,
          left: rect.left,
          background: 'var(--primary-header-bg)'
        };

    return (
      <div name="AutoCompleteGlobalSearch_Portal" style={style}>
        {searchedAssets.length === 0 && searchedCollections.length === 0 ? (
          searchString ? (
            <div>
              <h4 style={styles.searchContainer.searchHeader}>
                {this.state.loading ? <span className="dot">{t('SUGGESTING')}</span> : t('NO_MATCH_FOUND')}
              </h4>
              <Separator className="top" />
            </div>
          ) : null
        ) : (
          <div>
            <dl style={styles.searchContainer.searchList} ref="searchResults">
              {this.state.loading ? (
                <div>
                  <h4 style={styles.searchContainer.searchHeader}>
                    {this.state.loading ? <span className="dot">{t('SUGGESTING')}</span> : t('NO_MATCH_FOUND')}
                  </h4>
                  <Separator className="top" />
                </div>
              ) : null}
              {recentSearches.length && this.state.query && this.state.query.length === 0 ? (
                <dt>
                  <h4 style={styles.searchContainer.searchHeader}>{t('RECENT_SEARCHES')}</h4>
                </dt>
              ) : null}
              {this.state.query && this.state.query.length === 0 ? this.renderRecentSearches(recentSearches) : null}
              {searchedAssets.length ? (
                <dt>
                  <h4 style={styles.searchContainer.searchHeader}>{t('ASSETS')}</h4>
                </dt>
              ) : null}
              {this.renderSearchedAssetsList(searchedAssets)}
              {searchedCollections.length ? (
                <dt>
                  <h4 style={styles.searchContainer.searchHeader}>{t('COLLECTIONS')}</h4>
                </dt>
              ) : null}
              {this.renderSearchedCollectionsList(searchedCollections)}
            </dl>
            <Separator className="top" />
          </div>
        )}
        <Button
          className="advanced-search"
          type="LINK"
          onClick={this.onAdvancedSearchClick}
          text={t('ADVANCE_SEARCH.ADVANCE_SEARCH')}
        />
        {searchedAssets.length > 0 || searchedCollections.length > 0 ? (
          <div>
            <Separator className="bottom" />
            <div className="see-more">{t('PRESS_ENTER_TO_SEE_ALL_RESULTS')}</div>
          </div>
        ) : null}
      </div>
    );
  }

  renderRecentSearches(recentSearches) {
    return recentSearches.slice(0, 3).map((searchString, index) => {
      return (
        <dd key={index} onMouseDown={(e) => this.onSearchedAssetSelect(searchString)}>
          <TextWithEllipsisFileExtension title={searchString} text={searchString} />
        </dd>
      );
    });
  }

  renderSearchedAssetsList(assets) {
    return assets.slice(0, this.props.maxSearchResult).map((asset) => {
      return (
        <dd
          key={asset.getFuid()}
          data-fuid={asset.getFuid()}
          data-rrn={asset.getRrn()}
          onMouseDown={(e) => this.onSearchedAssetSelect(asset.getName())}>
          <div data-title={asset.getName()}>
            <TextWithEllipsisFileExtension title={asset.getName()} text={asset.getName()} />
          </div>
        </dd>
      );
    });
  }

  renderSearchedCollectionsList(collections) {
    return collections.slice(0, this.props.maxSearchResult).map((collection) => {
      const matchtype = collection._matchType;
      let iconStyle = null;
      let title = null;

      if (matchtype) {
        iconStyle = matchtype === 2 ? 'fa fa-file-o' : null;
        title = matchtype === 2 ? t('MATCHING_FILES_FOUND_IN_THIS_COLLECTION') : null;
      }

      return (
        <Dd
          className="searched-collection"
          key={collection.getCollectionId()}
          onMouseDown={(e) => this.onSearchedCollectionSelect(e)}
          data-id={collection.getCollectionId()}>
          <Div style={{ flexGrow: '3', width: '280px', maxWidth: '280px' }}>
            <TextWithEllipsisFileExtension title={collection.getName()} text={collection.getName()} />
          </Div>
          <span className={iconStyle} style={{ color: 'var(--primary-style-color)', flexGrow: '1' }} />
        </Dd>
      );
    });
  }

  render() {
    const { searchFocus, query } = this.state;
    const value = this.refs.search && this.refs.search.value !== undefined ? this.refs.search.value : '';
    const styles = getStyle();
    const rect = window.searchBlock ? window.searchBlock.getBoundingClientRect() : {};

    return (
      <form id="searchBlock" className="flex-row" onSubmit={(event) => this.onSearchSubmit(event)}>
        <input
          ref="search"
          onKeyUp={this.onKeyUp}
          onKeyDown={this.onKeyPress}
          onFocus={this.onSearchFocus}
          onBlur={this.onSearchBlur}
          defaultValue={query}
          type="text"
        />
        {/* <span className={classNames('placeholder', { hide: searchFocus || value })}>{t('SEARCH_PLACEHOLDER')}</span> */}
        {query && query.length ? (
          <div
            className="close-icon"
            style={styles.searchContainer.closeSearch}
            onClick={() => this.resetSearchBox()}
          />
        ) : (
          <span className={classNames('placeholder', { hide: searchFocus || value })}>{t('SEARCH_PLACEHOLDER')}</span>
        )}
        <div
          style={styles.searchContainer.searchIcon}
          className="icon"
          onClick={(event) => this.onSearchSubmit(event)}
        />
        {searchFocus ? (
          <Portal defaultOpen={true} closeOnEsc>
            <div className="transparent-background" style={{ top: rect.bottom }}>
              <div>{this.renderAutoCompleteData()}</div>
            </div>
          </Portal>
        ) : null}
      </form>
    );
  }
}

function getStyle() {
  return {
    searchContainer: {
      searchDropDown: {
        width: '400px',
        position: 'absolute',
        background: '#FFFFFF',
        top: '58px',
        boxShadow: '0 15px 30px -10px rgba(0,0,0,0.20)',
        borderRadius: '0 0 3px 3px',
        padding: '10px 0'
      },
      searchHeader: {
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '15px',
        color: 'var(--header-basic-font)',
        letterSpacing: '1px',
        paddingLeft: '20px',
        margin: '10px 0',
        textTransform: 'uppercase'
      },
      searchList: {
        margin: '8px 0',
        paddingLeft: 0
      },
      closeSearch: {
        backgroundImage: ThemeSetup.getBackgroundURL('close_search.svg')
      },
      searchIcon: {
        backgroundImage: ThemeSetup.getBackgroundURL('search.svg'),
        cursor: 'pointer'
      }
    }
  };
}
